import axios from "axios";

import { VndErrors } from "@/types/auth";

export const hasMessage = (e: any): e is Error => typeof e.message === "string";

export function isVndError(obj: unknown): obj is VndErrors {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "logref" in obj &&
    typeof obj.logref === "string" &&
    "message" in obj &&
    typeof obj.message === "string"
  );
}

/** An error carrying the backend's own message and error contract, instead of a generic string. */
export class ApiError extends Error {
  readonly errorType?: string;
  readonly fieldErrors?: Record<string, string>;
  readonly logref?: string;
  readonly status?: number;

  constructor(
    message: string,
    options?: {
      errorType?: string;
      fieldErrors?: Record<string, string>;
      logref?: string;
      status?: number;
      cause?: unknown;
    },
  ) {
    super(message, { cause: options?.cause });
    this.name = "ApiError";
    this.errorType = options?.errorType;
    this.fieldErrors = options?.fieldErrors;
    this.logref = options?.logref;
    this.status = options?.status;
  }
}

/**
 * A company-domains save refused until the caller confirms that, in an encrypted company, people with
 * addresses in `domains` can no longer sign in with a password.
 */
export class DomainRemovalNeedsConfirmationError extends ApiError {
  readonly domains: string[];

  constructor(message: string, domains: string[]) {
    super(message, { errorType: "DOMAIN_REMOVAL_NEEDS_CONFIRMATION", status: 409 });
    this.name = "DomainRemovalNeedsConfirmationError";
    this.domains = domains;
  }
}

/** Why a creation was refused although the form was valid: the address belongs to an existing account
 * (`USER_EMAIL_TAKEN`) or to one whose creation is already scheduled (`USER_CREATION_PENDING`). `userId`
 * names that account, so the form can link to it; a creation SCIM, a sync or a file scheduled has none yet. */
export class UserCreationConflictError extends ApiError {
  readonly userId?: string;

  constructor(
    message: string,
    errorType: "USER_EMAIL_TAKEN" | "USER_CREATION_PENDING",
    userId?: string | null,
  ) {
    super(message, { errorType, status: 409 });
    this.name = "UserCreationConflictError";
    this.userId = userId ?? undefined;
  }
}

const hasErrorDescription = (data: unknown): data is { error_description: string } =>
  typeof data === "object" &&
  data !== null &&
  "error_description" in data &&
  typeof data.error_description === "string" &&
  data.error_description !== "";

/**
 * The `error_description` of an OAuth2 error answer, which the token endpoint uses to say why it refused
 * (Run as, support codes). Undefined for any other failure, so the caller picks its own fallback instead
 * of axios's "Request failed with status code 403".
 */
export function oauth2ErrorDescription(e: unknown): string | undefined {
  if (axios.isAxiosError(e)) {
    const data: unknown = e.response?.data;
    if (hasErrorDescription(data)) {
      return data.error_description;
    }
  }
  return undefined;
}

const isProblemDetail = (data: unknown): data is { detail: string } =>
  typeof data === "object" && data !== null && "detail" in data && typeof data.detail === "string";

/**
 * Converts a failed request into an `ApiError`. `VndErrors` bodies keep their `errorType` and
 * `fieldErrors` so a caller can put a 409 on the right form field; Spring's `ProblemDetail`
 * (returned for out-of-range enums, which fail during deserialisation) keeps its `detail`.
 */
export function toApiError(e: unknown, fallbackMessage: string): ApiError {
  if (axios.isAxiosError(e)) {
    const data: unknown = e.response?.data;
    const status = e.response?.status;
    if (isVndError(data)) {
      return new ApiError(data.message, { ...vndErrorDetails(data), status, cause: e });
    }
    if (isProblemDetail(data)) {
      return new ApiError(data.detail, { status, cause: e });
    }
    return new ApiError(fallbackMessage, { status, cause: e });
  }
  return new ApiError(fallbackMessage, { cause: e });
}

/**
 * Like `toApiError`, but always says `message` instead of the backend's: the global handler's
 * catch-all answers an unexpected failure with the exception's own text, which is no message for a
 * user. The status, `errorType`, `fieldErrors` and `logref` still travel with it, so a screen can
 * tell a 403 from a 404 or a 409.
 */
export function apiErrorWithMessage(e: unknown, message: string): ApiError {
  if (axios.isAxiosError(e)) {
    const data: unknown = e.response?.data;
    return new ApiError(message, {
      ...(isVndError(data) ? vndErrorDetails(data) : {}),
      status: e.response?.status,
      cause: e,
    });
  }
  return new ApiError(message, { cause: e });
}

const vndErrorDetails = (data: VndErrors) => ({
  errorType: data.errorType ?? undefined,
  fieldErrors: data.fieldErrors ?? undefined,
  logref: data.logref,
});
